// The audit trail.
//
// Every privileged action, with its actor. AuditLogger has written these rows
// since the beginning — 39 call sites, in the same transaction as the change
// they describe — and nothing could read them back, so the handoff's promise
// that "every admin action writes an audit entry with actor and reason" was
// true and unverifiable from the product.
//
// Read-only, deliberately and visibly: the page offers no edit or delete
// affordance because the API has none either.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState } from '../../components/shared/primitives';
import { ADMIN_NAV } from '../_navs';
import { auditLogs, type AuditEntry } from '../../lib/api';

/** Tone by what the action does, so scanning finds the consequential rows. */
function toneFor(action: string): string {
  if (action.startsWith('DISPUTE_')) return 'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]';
  if (action.startsWith('PAYOUT_') || action.includes('REFUND')) return 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]';
  if (action.startsWith('MERCHANT_KYB')) return 'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]';
  if (action.startsWith('FX_') || action.startsWith('FEE_')) return 'bg-[#EAF0FE] text-[#063B9E]';
  return 'bg-[var(--color-panel)] text-[var(--color-secondary)]';
}

export function AdminAuditLogPage() {
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const q = useQuery({
    queryKey: ['admin', 'audit', filter, page],
    queryFn: () => auditLogs.list(filter || undefined, page),
  });

  const entries = q.data?.entries ?? [];
  const filters = q.data?.filters ?? [];
  const meta = q.data?.meta;

  return (
    <PortalShell
      nav={ADMIN_NAV}
      activeId="audit"
      title="Audit log"
      subtitle="Every privileged action, with a reason. Entries can’t be edited or deleted."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {['', ...filters].map((f) => (
            <button
              key={f || 'all'}
              type="button"
              onClick={() => { setFilter(f); setPage(1); }}
              aria-pressed={filter === f}
              className={`rounded-xl border px-4 py-2 text-[13px] font-extrabold transition-colors ${
                filter === f
                  ? 'border-transparent bg-[var(--color-ink)] text-white'
                  : 'border-[var(--color-border)] bg-white text-[var(--color-secondary)] hover:text-[var(--color-ink)]'
              }`}
            >
              {f || 'All'}
            </button>
          ))}
          {meta && (
            <span className="ml-auto self-center text-[12.5px] text-[var(--color-muted)]">
              {meta.total} {meta.total === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>

        <Card padding="p-0">
          {q.isLoading ? (
            <div className="py-16 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>
          ) : entries.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={FileText}
                title="Nothing recorded yet"
                body={filter ? 'No entries match this filter.' : 'Privileged actions will appear here as they happen.'}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead className="bg-[#FAFBFE] text-[11px] font-extrabold uppercase tracking-[0.7px] text-[var(--color-muted)]">
                  <tr>
                    <th className="px-5 py-2.5">Time</th>
                    <th className="px-3 py-2.5">Actor</th>
                    <th className="px-3 py-2.5">Action</th>
                    <th className="px-3 py-2.5">Target</th>
                    <th className="px-5 py-2.5">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e: AuditEntry) => (
                    <tr key={e.id} className="border-t border-[#F2F4F9] align-top">
                      <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-[var(--color-muted)]">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-[13px] text-[var(--color-ink)]">
                        {/* A system action has no actor. Saying so beats an
                            empty cell that reads as missing data. */}
                        {e.actor ?? <span className="text-[var(--color-muted)]">system</span>}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-block whitespace-nowrap rounded-[7px] px-2 py-1 font-mono text-[11px] font-bold ${toneFor(e.action)}`}>
                          {e.action}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 font-mono text-[12.5px] text-[var(--color-body)]">
                        {e.entity_type} #{e.entity_id}
                      </td>
                      <td className="px-5 py-3 text-[13px] text-[var(--color-secondary)]">
                        {e.reason ?? <span className="text-[var(--color-muted)]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-[12.5px] font-semibold text-[var(--color-body)] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" /> Newer
            </button>
            <span className="text-[12.5px] text-[var(--color-muted)]">Page {meta.page} of {meta.last_page}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              disabled={page >= meta.last_page}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-[12.5px] font-semibold text-[var(--color-body)] disabled:opacity-40"
            >
              Older <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
